/**
 * `currentRouterPath()` — read `window.location.pathname + search` and strip
 * the Vite `BASE_URL` prefix so the result is a router-internal path that
 * `navigate({ to })` can safely accept.
 *
 * Why: Employee Web is served at Vite `base: "/me/"` AND TanStack Router is
 * mounted with `basepath: "/me"`. The router prepends its basepath to every
 * `to`, so a redirect param captured from `window.location.pathname` (which
 * INCLUDES the basepath) round-trips through `navigate({ to })` and double-
 * prefixes — `/me/profile` becomes `/me/me/profile`. The router-internal
 * `location.pathname` (used by `requireAuth`) is already basepath-stripped,
 * so it doesn't have this problem; only `window.location` callers do.
 *
 * BASE_URL is always `/` for apps without a `base` (Link Admin), so the
 * strip is a no-op there. For HR Admin (`/admin/`) and Employee Web (`/me/`)
 * it removes the prefix without the trailing slash.
 */
export function currentRouterPath(): string {
  const baseUrl = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "/";
  const fullPath = window.location.pathname + window.location.search;
  if (baseUrl === "/" || baseUrl.length === 0) return fullPath;
  const prefix = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  if (fullPath === prefix) return "/";
  if (fullPath.startsWith(prefix + "/")) return fullPath.slice(prefix.length);
  return fullPath;
}
